import { CALENDAR_TYPE, eventActivityType, eventTrainingList } from '../../constants/myBlockMonth';
import { convertToDateObject, convertToDateTime } from '../../helpers/dateHelpers';
import { formatNumToTime } from '../../helpers/timeHelpers';

export const DEFAULT_STRIP_CAL_HEIGHT_LEVEL_1_REM = 20.3;
export const DEFAULT_STRIP_CAL_HEIGHT_LEVEL_2_REM = 24.3;
export const SPECIAL_EVENT_TYPES = [
  eventActivityType.DSP,
  eventActivityType.GDO,
  eventActivityType.GDO6,
  eventActivityType.PIT,
  eventActivityType.REASSIGN,
  eventActivityType.SDO,
  eventActivityType.SICK,
  eventActivityType.SICK_INS,
  eventActivityType.TDO,
  eventActivityType.TDO6,
  eventActivityType.VACATION,
  eventActivityType.VAC2448];

/**
 * Add the pairing's object in the pairingList response to the blockMonthActivities response
 * @param blockMonthActivities 
 * @param pairingsList 
 */
export const addPairingDetailToActivities = (blockMonthActivities, pairingsList) => {
  blockMonthActivities.forEach(activity => {
    if(activity.activityType === eventActivityType.PAIRING) {

      /* Find the pairing from the activities in the pairingList by matching the activities startDateTime to the pairingLists pairingDates[0] time */
      const activityStartDateTime = convertToDateTime(activity.start_date, '00:00');
      const pairing = pairingsList.filter(pairing => pairing.pairingId === activity.activityId && convertToDateTime(pairing.pairingDates[0], '00:00') === activityStartDateTime)[0];
      
      if(pairing) activity.pairingDetails = {...pairing};
    }
  })
}

/** 
 * @param durationA 
 * @param durationB 
 * @returns the summation of the duration (string) of two RAP's
 */
 const addEventsDuration = (durationA: string, durationB: string) => {
  if(!durationA || !durationB) return '';
  const durAHrs = Number(durationA.split(' ')[0]);
  const durBHrs = Number(durationB.split(' ')[0]);
  const durADaysRemoveParentheses = durationA.split('(').pop()?.split(')')[0];
  const durBDaysRemoveParentheses = durationB.split('(').pop()?.split(')')[0];

  const durADays = durADaysRemoveParentheses?.slice(0,durADaysRemoveParentheses.indexOf('day')).trim();
  const durBDays = Number(durBDaysRemoveParentheses?.slice(0,durBDaysRemoveParentheses.indexOf('day')).trim());

  return `${durAHrs + durBHrs} hrs (${Number(durADays) + durBDays} day)`;
} 

/**
 * Add reserve events (events on empty calendar days) to the planned calendar 
 * @param blockMonthActivities 
 * @param myBlockMonthData 
 */
export const addReserveToPlanned = (blockMonthActivities, myBlockMonthData) => {
  const blockDate = {
    start: convertToDateObject(myBlockMonthData.blockDates.start, '00:00'),
    end: convertToDateObject(myBlockMonthData.blockDates.end, '23:59')
  }
  let eventTimezone = 'EDT'

  /* Create an activityDates array where each element is an object of the activities start and end time */
  const activities = blockMonthActivities.map(activity => {
    if(activity.timezone) eventTimezone = activity.timezone; 
    return {
      ...activity,
      startTime: convertToDateTime(activity.start_date, '00:00'),
      endTime: convertToDateTime(activity.end_date, '23:59'),
    }
  })

  const startDate = new Date(+blockDate.start);

  /* disregardedDays variable is used to keep track of which consecutive days have been combined into 1 event 
  and to not show that event on that date on the calendar because it has already been handled previously */
  let disregardedDays: Array<string> = []; 

  /* starting on the block start date, until the block end date, iterating each day 
  check whether an activity exists on this day but comparing the day against activityDates */
  for(let day = startDate; day <= blockDate.end; day.setDate(day.getDate() + 1)) {

    /* if NOT(there is an activity that spans within this day and that activity does not end at midnight) */
    if(!activities.filter(activity => activity.activityType !== eventActivityType.HOLIDAY 
                          && day.getTime() >= activity.startTime && day.getTime() <= activity.endTime 
                          && day.getTime() !== convertToDateTime(activity.end_date, formatNumToTime(activity.checkOut.value))).length)
    {

      /* daySpan variable is used to combine separate day events into 1 event with the correct date span */
      let daySpan = 1; 

      let endDay = getDateString(day);

      /* check the number of consecutive empty calendar days */
      const followingDay = new Date(+day);
      followingDay.setDate(followingDay.getDate() + 1)
      for(let nextDay = followingDay; nextDay <= blockDate.end; nextDay.setDate(nextDay.getDate() + 1)) { 

        /* if there are no activities on this day, increment the daySpan, set the endDay to the current day and add this day to the disregarded events */
        if(activities.findIndex(activity => nextDay.getTime() >= activity.startTime && nextDay.getTime() <= activity.endTime) === -1) {
          daySpan++;
          endDay = getDateString(nextDay); 
          disregardedDays.push(getDateString(nextDay));
        } else break; 
      }

      if(!disregardedDays.includes(getDateString(day))) {
        blockMonthActivities.push({
          activityId: 'RSV',
          activityType: 'RSV',
          checkIn: {value: 0, deviation: 0 },
          checkOut: {value: 1439, deviation: 0 },
          credit: '', 
          days: daySpan,
          duration: `${24*daySpan} hrs (${daySpan} days)`,
          endTime: '',
          end_date: endDay,
          instructor: '',
          layover: '',
          location: '',
          remarks: '',
          startDateDaysTime: day.getTime(),
          startTime: '',
          start_date: getDateString(day),
          timezone: eventTimezone,
          trainees: [],
          trainingName: '',
          unique_id: Math.max(...blockMonthActivities.map(o => o.unique_id)) + 10
        })  
      }
    }
  }
  blockMonthActivities.sort((x, y) => x.startDateDaysTime - y.startDateDaysTime);
}

/**
 * Set the height level for interesecting special events
 * either 2 or 3 (Pairings/Training/RAP/RAPH/RSV are all level 1 and do not intersect)
 * @param specialEventList - sorted by startDate timestamp already
 */
export const assignSpecialEventStripCalendarHeightLevel = (specialEventList) => {

  /* set the initialy height level to 0 for every special event */
  specialEventList.forEach(event => event.stripHeightLevel = 0);

  specialEventList.forEach(event => {
      const eventStartTime = convertToDateTime(event.start_date, formatNumToTime(event.checkIn.value));
      const eventEndTime = convertToDateTime(event.end_date, formatNumToTime(event.checkOut.value));

      /* set eventsToCompare variable to an array of the elements minus the current element */
      const eventsToCompare = specialEventList.filter(compareEvent => compareEvent.unique_id !== event.unique_id);

      eventsToCompare.forEach(compareEvent => {
          const compareEventStartTime = convertToDateTime(compareEvent.start_date, formatNumToTime(compareEvent.checkIn.value));

          /* if the compare event starts at the same time as the event or the compare event falls inbetween the start and end time of the event */
          if(compareEventStartTime === eventStartTime || (compareEventStartTime > eventStartTime && compareEventStartTime < eventEndTime)) {

              /* ignore if the events level has already been set */
              if(event.stripHeightLevel === 0) event.stripHeightLevel = 2;

              /* ignore if the compareEvent's level has already been set
              if the event's level is set to 2, set the compareEvent's leve to 3 vice-versa */
              if(compareEvent.stripHeightLevel === 0) compareEvent.stripHeightLevel = event.stripHeightLevel === 2 ? 3 : 2;
          } 
      })

      /* set the event level to 2 if no intersecting compare event found */
      if(event.stripHeightLevel === 0) event.stripHeightLevel = 2;
  })
}

/**
 * Determine whether to show the data or display the No Data view
 * @param myBlockMonthContext 
 * @returns true if necessary data is available
 */
export const checkDataFromBlockMonthApiResponse = (myBlockMonthContext) => {
  if(!Object.keys(myBlockMonthContext.myBlockMonthData).length || !myBlockMonthContext.myBlockMonthData.BLOCK_TYPE_MNT) return false;

  if(myBlockMonthContext.myBlockMonthData?.blockList && 
     myBlockMonthContext.myBlockMonthData?.activities?.length && 
     myBlockMonthContext.myBlockMonthData?.blockDates && 
     myBlockMonthContext.myBlockMonthData?.blockDates?.start && 
     myBlockMonthContext.myBlockMonthData?.blockDates?.end) return true;

  return false;
}

/**
 * Combine RAP events that have an end release and an immediate RAP after
 * @param blockMonthActivities 
 */
 export const combineRAPsWithMiddleRelease = (blockMonthActivities) => {

  /* id's of RAP's that will be combined with the prior RAP, to be removed from the activities list */
  const rapUniqueIdsToDelete: number[] = [];
  const updatedBlockMonthActivities = blockMonthActivities.map(activity => {
    if(activity.activityType === eventActivityType.RAP) {

      /* find a release where the start date and time are the same as the currently iterated RAP's end date and time */
      const releaseIndex = blockMonthActivities.findIndex(release => {
        return(convertToDateTime(release.start_date, formatNumToTime(release.checkIn.value)) === convertToDateTime(activity.end_date, formatNumToTime(activity.checkOut.value)));
      })

      /* if such a release event exists */
      if(releaseIndex !== -1) {

        /* find a RAP (other than the original RAP) where the RAP's start date and time are the same as the release's end date and time  */
        const combiningRapIndex = blockMonthActivities.findIndex(act => {
          return(act.activityType === eventActivityType.RAP && act.unique_id !== activity.unique_id && convertToDateTime(blockMonthActivities[releaseIndex].end_date, formatNumToTime(blockMonthActivities[releaseIndex].checkOut.value))  === convertToDateTime(act.start_date, formatNumToTime(act.checkIn.value)));
        })

        /* if such a RAP event exists, add the unique_id value to the deleted array and change the currently iterated RAP's properties */
        if(combiningRapIndex !== -1) {
          rapUniqueIdsToDelete.push(blockMonthActivities[combiningRapIndex].unique_id); 
          return {
            ...activity, 
            checkOut: {...blockMonthActivities[combiningRapIndex].checkOut},
            days: activity.days + blockMonthActivities[combiningRapIndex].days - 1,
            duration: addEventsDuration(activity.duration, blockMonthActivities[combiningRapIndex].duration),
            end_date: blockMonthActivities[combiningRapIndex].end_date
          }
        }
        return activity; 
      }
      return activity; 
    }
    return activity; 
  })

  /* filter out the unnecessary RAP's */
  return updatedBlockMonthActivities.filter(activity => !rapUniqueIdsToDelete.includes(activity.unique_id));
}

/**
 * Combine G/TDO6 events that preceede directly a G/TDO event
 * @param blockMonthActivities 
 */
export const combineDO6 = (blockMonthActivities, activityType6, activityType) => {
  let indexesToRemove = [] as number[];
  blockMonthActivities.forEach(activity => {
    if(activity.activityType === activityType6) {
      const activityEndDateTime = convertToDateTime(activity.end_date, formatNumToTime(activity.checkOut.value));
      const activityEndDateTime1 = convertToDateTime(activity.end_date, formatNumToTime(activity.checkOut.value+1));
      
      /* find a DO event where the start date and time are the same as the end date and time of DO+6 +- 1 minute */
      const doEventIndex = blockMonthActivities.findIndex(a => {
        return(a.activityType === activityType && (convertToDateTime(a.start_date, formatNumToTime(a.checkIn.value)) === activityEndDateTime || convertToDateTime(a.start_date, formatNumToTime(a.checkIn.value)) === activityEndDateTime1));
      })

      /* if such a DO event exists, update the RSV event props */
      if(doEventIndex !== -1) {
        activity.checkOut.value = blockMonthActivities[doEventIndex].checkOut.value;
        activity.days += 1;
        activity.duration = addEventsDuration(blockMonthActivities[doEventIndex].duration, activity.duration);
        activity.end_date = blockMonthActivities[doEventIndex].end_date;
        indexesToRemove.push(doEventIndex);
      }
    }
  })
  return blockMonthActivities.filter((a, i) => !indexesToRemove.includes(i));
}

/** 
 * If the event's end date is greater than the blockEnd date and if the end date does not end on midnight the next day
 * @returns 
 */
export const checkEventCarryOut = (blockDateEnd, event) => {
  let oneMinuteAfterBlockEndDate = new Date(+blockDateEnd);
  oneMinuteAfterBlockEndDate.setMinutes(oneMinuteAfterBlockEndDate.getMinutes() + 1);
  const eventEndDate = convertToDateObject(event.end_date, event.checkOut.value);
  return convertToDateObject(event.end_date, '23:59') > blockDateEnd && eventEndDate.getTime() !== oneMinuteAfterBlockEndDate.getTime();
}

export const getCapsuleClassName = (event, type, blockDate?) => {
  let className = type === CALENDAR_TYPE.STRIP ? 'event__capsule' : 'capsule';
  if(event.carryIn) {
    if(blockDate) {
      let dayBeforeBlockDateStart = new Date(+blockDate.start);
      dayBeforeBlockDateStart.setDate(dayBeforeBlockDateStart.getDate()-1);
      if(convertToDateTime(event.start_date, '00:00') !== dayBeforeBlockDateStart.getTime()) className += ' capsule--carry-in';
    } else {
      className += ' capsule--carry-in';
    }
  }
  if(event.carryOut) {
    if(blockDate) {
      let dayAfterBlockDateEnd = new Date(+blockDate.end);
      dayAfterBlockDateEnd.setDate(dayAfterBlockDateEnd.getDate()+1);
      if(convertToDateTime(event.end_date, '23:59') !== dayAfterBlockDateEnd.getTime()) className += ' capsule--carry-out';
    } else {
      className += ' capsule--carry-out';
    }
  }
  if(event.activityType === eventActivityType.DSP) className += ' capsule--dsp'
  if(type === CALENDAR_TYPE.STRIP && event.specialEvent) className += ' capsule--special'
  return className;
}

export const getClassNameForEarlyOrLate = (deviation, actualView, className) => {
  if(!actualView) return className;

  switch(Math.sign(deviation)) {
      case 0 : {
          return className;
      }
      case 1 : {
          return className + ' event__time--late';
      }
      case -1 : {
          return className + ' event__time--early';
      }
      default : {
          return className;
      }
  }
}

const getDateString = (day) => {
  return `${day.getFullYear()}/${day.getMonth() + 1 < 10 ? '0' + (day.getMonth() + 1).toString(): day.getMonth() + 1}/${day.getDate() < 10 ? '0' + (day.getDate()).toString() : day.getDate()}`;
}

export const getEventBaseLocation = (event, profileContext) => {
  if(!event && !event.activityType && !event.activityId) {
    return;
  } else if(event.activityType === eventActivityType.TRAINING) {
    return event.activityId === eventTrainingList.SIM ? profileContext.profile.results?.baseLocation : event.activityId === eventTrainingList.SIMTRAVEL? 'Away from base':'';
  }
}

export const getEventCapsuleWidth = (calendarElementWidth, startDateTime, endDateTime) => {
  const hours = Math.abs(startDateTime - endDateTime)/3.6e6;
  const days = hours/24;
  return days*calendarElementWidth; 
}

export const getEventColor = (activityType) => {
  switch (activityType) {
      case eventActivityType.PAIRING: case eventActivityType.RAP: case eventActivityType.RAPH: case eventActivityType.RESERVE: {
          return '#005078';
      }
      case eventActivityType.TRAINING: case eventActivityType.ITC: {
          return '#ad7b82';
      }
      case eventActivityType.VACATION: case eventActivityType.SDO: case eventActivityType.VAC2448: {
          return '#e89b20';
      }
      case eventActivityType.SICK: case eventActivityType.SICK_INS: {
          return '#52c6aa';
      }
      case eventActivityType.PIT: case eventActivityType.REASSIGN: {
          return '#ff5b60';
      }
      case eventActivityType.DSP: {
          return '#fff';
      }
      case eventActivityType.GDO: case eventActivityType.GDO6: case eventActivityType.TDO: case eventActivityType.TDO6: {
          return '#e3d9ca';
      }
      default: {
          return '#737373';
      }
  }
}

export const getCarryOverEventColor = (activityType) => {
  switch (activityType) {
      case eventActivityType.PAIRING: case eventActivityType.RAP: case eventActivityType.RAPH: {
          return 'rgba(0, 80, 120, 0.4)';
      }
      case eventActivityType.TRAINING: case eventActivityType.ITC: {
          return 'rgba(173, 123, 130, 0.4)';
      }
      case eventActivityType.VACATION: case eventActivityType.SDO: case eventActivityType.VAC2448: {
          return 'rgba(232, 155, 32, 0.4)';
      }
      case eventActivityType.SICK: case eventActivityType.SICK_INS: {
          return 'rgba(82, 198, 170, 0.4)';
      }
      case eventActivityType.PIT: case eventActivityType.REASSIGN: {
          return 'rgba(255, 91, 96, 0.4)';
      }
      case eventActivityType.DSP: {
          return 'rgba(255, 255, 255, 0.4)';
      }
      case eventActivityType.GDO: case eventActivityType.GDO6: case eventActivityType.TDO: case eventActivityType.TDO6: {
          return 'rgba(227, 217, 202, 0.4)';
      }
      default: {
          return 'rgba(115, 115, 115, 0.4)';
      }
  }
}

export const getEventName = (event) => {
  switch(event.activityType) {
    case eventActivityType.DSP: 
      return 'DSP';
        
    case eventActivityType.GDO: 
      return 'GDO';

    case eventActivityType.GDO6: 
      return 'GDO+6';

    case eventActivityType.ITC: 
      return 'ITC';

    case eventActivityType.PAIRING:
      return event.activityId;

    case eventActivityType.PIT: 
      return 'PIT';

    case eventActivityType.REASSIGN: 
      return 'REASSIGN';

    case eventActivityType.RAP: 
      return 'RAP';
      
    case eventActivityType.RAPH: 
      return 'RAP-H';

    case eventActivityType.RESERVE: 
      return 'RSV';
        
    case eventActivityType.SDO: 
      return 'SDO';
    
    case eventActivityType.SICK: 
      return 'SICK';

    case eventActivityType.SICK_INS: 
      return 'GDIP';

    case eventActivityType.TDO: 
      return 'TDO';

    case eventActivityType.TDO6: 
      return 'TDO+6';

    case eventActivityType.TRAINING: 
      return getTrainingEventName(event);
      
    case eventActivityType.VACATION: 
      return 'VAC';
      
    case eventActivityType.VAC2448: 
      return 'O24/48';

    default : 
      return 'N/A';
      
  }
}

export const getEventSubTitle = (event) => {
  switch (event.activityType) {
    case eventActivityType.DSP:
      return 'Displaced Pairing';
    case eventActivityType.GDO: case eventActivityType.GDO6:
      return 'Guaranteed Days Off';
    case eventActivityType.ITC:
      return 'Intro to Command';
    case eventActivityType.PIT: 
      return 'Pairing Integrity';
    case eventActivityType.REASSIGN:
      return 'Reassignment Period';
    case eventActivityType.RESERVE:
      return 'Reserve Days';
    case eventActivityType.RAP:
      return 'Reserve Availability Period';
    case eventActivityType.RAPH:
      return 'Reserve Availability Period - HOLD';
    case eventActivityType.SDO:
      return 'Special Days Off';
    case eventActivityType.SICK:
      return 'Sick Day';
    case eventActivityType.SICK_INS:
      return 'Group Disablity Insurance Plan';
    case eventActivityType.TDO: case eventActivityType.TDO6:
      return 'Technical Days Off';
    case eventActivityType.TRAINING:
      return getTrainingEventSubTitle(event);
    case eventActivityType.VACATION:
      return 'Vacation';
    case eventActivityType.VAC2448:
      return 'Optional 24/48';
    default:
      return 'N/A';
  }
};

export const getEventTimezone = (timezone: string) => {
  let formattedTimezone = timezone ?? ''; 
  if(timezone) {
    formattedTimezone = timezone.includes('S') ? timezone.replace('S', '') : timezone.includes('D') ? timezone.replace('D', '') : timezone;
  }
  return formattedTimezone; 
}

/**
 * @returns the width of an event capsule in percentage relative to the total width of the calendar
 */
export const getStripCalendarCapsuleWidth = (carryIn, carryOut, event, blockDate, calendarElementWidth) => {

  let startDateTime = convertToDateTime(event.start_date, formatNumToTime(event.checkIn.value));
  let endDateTime = convertToDateTime(event.end_date, formatNumToTime(event.checkOut.value));

  if(carryOut) {
      let dayAfterBlockDateEnd = new Date(+blockDate.end);
      dayAfterBlockDateEnd.setDate(dayAfterBlockDateEnd.getDate() + 1);
      endDateTime = dayAfterBlockDateEnd.getTime();
  }

  if(event.hasStartRelease) {
      startDateTime = convertToDateTime(event.release[0].start_date, formatNumToTime(event.release[0].checkIn.value));
  }

  if(event.hasEndRelease) {
      endDateTime = convertToDateTime(event.release[event.release.length-1].end_date, formatNumToTime(event.release[event.release.length-1].checkOut.value));
  }

  if(carryIn) {
      let dayBeforeBlockDateStart = new Date(+blockDate.start);
      dayBeforeBlockDateStart.setDate(dayBeforeBlockDateStart.getDate() - 1);
      startDateTime = dayBeforeBlockDateStart.getTime();
  }
  
  return getEventCapsuleWidth(calendarElementWidth, startDateTime, endDateTime);
}

export const getStripCalendarHeight = (stripCalHeightLevel) => {
  switch(stripCalHeightLevel) {
      case 1 : case 2 : {
          return DEFAULT_STRIP_CAL_HEIGHT_LEVEL_1_REM;
      }
      case 3 : {
          return DEFAULT_STRIP_CAL_HEIGHT_LEVEL_2_REM;
      }
      default : {
          return DEFAULT_STRIP_CAL_HEIGHT_LEVEL_1_REM;
      }
  }
}

export const getTrainingEventName = (event) => {
  switch (event.activityId) {
    case eventTrainingList.SIM : case eventTrainingList.SIMTRAVEL :
      return 'SIM';
    case eventTrainingList.ART :
      return 'ART';
    case eventTrainingList.SCHOOL :
      return 'SCHOOL';
    case eventTrainingList.IOE :
      return 'IOE';
    default:
      return event.activityId;
  }
};

export const getTrainingEventSubTitle = (event) => {  
  switch (event.activityId) {
    case eventTrainingList.SIM : case eventTrainingList.SIMTRAVEL :
      return 'Simulator';
    case eventTrainingList.ART :
      return 'Annual Recurrent Training';
    case eventTrainingList.SCHOOL :
      return 'Aircraft Ground School Course';
    case eventTrainingList.IOE :
      return 'Initial Operating Experience';
    default:
      return event.activityId;
  }
};

export const getTrainingRouteDetails = (activityId, profileContext) => {
  return activityId === 'SIMLOCAL' ? getBaseLocation(profileContext) : activityId === 'SIMTRAVEL' ? 'AWAY' : '';
}

const getBaseLocation = (profileContext) => {
  if (!profileContext?.profile?.results?.baseLocation) {
    return '';
  }
  return profileContext.profile.results.baseLocation ? profileContext.profile.results.baseLocation : 'AWAY';
}

/**
 * If a GDO+6 event exists the day after a RSV event, extend the RSV event to the beginning of the GDO+6 event
 * @param blockMonthActivities 
 */
export const extendReserveToGDO6 = (blockMonthActivities) => {
  blockMonthActivities.forEach(activity => {
    if(activity.activityType === eventActivityType.RESERVE) {
      let dayAfterEndDay = convertToDateObject(activity.end_date, '00:00');
      dayAfterEndDay.setDate(dayAfterEndDay.getDate() + 1);

      /* find a GDO+6 event that start 1 day after the RSV event ends and has a checkIn time greater than 00:01 */
      const gdo6EventIndex = blockMonthActivities.findIndex(activities =>  (activities.activityType === eventActivityType.GDO6 || activities.activityType === eventActivityType.TDO6) && activities.start_date === getDateString(dayAfterEndDay) && activities.checkIn.value > 1);
      
      /* if such a GOD+6 event exists, update the RSV event props */
      if(gdo6EventIndex !== -1) {
        activity.checkOut.value = blockMonthActivities[gdo6EventIndex].checkIn.value - 1;
        activity.days += 1; 
        activity.duration= `${24*activity.days} hrs (${activity.days} days)`;
        activity.end_date = getDateString(dayAfterEndDay);
      }
    }
  })
}

/**
 * All activities come back in an array as the API response, 
 * FE job is to map the release activity to the correct pairing/rap activity and then remove that release activity from the activities array
 * Pairings: release at the beginning
 * RAP: release at beginning, middle, end 
 * @param blockMonthActivities 
 */
export const mapReleaseToParentActivity = (blockMonthActivities) => {
  const releaseActivities = blockMonthActivities.filter(activity => activity.activityType === eventActivityType.RELEASE);
  
  blockMonthActivities.forEach(activity => {
    const activityStartDateTime = convertToDateTime(activity.start_date, formatNumToTime(activity.checkIn.value));
    const activityEndDateTime = convertToDateTime(activity.end_date, formatNumToTime(activity.checkOut.value));

    /* for a pairing, find a release activity where the pairings startTime is equal to the release activity's endTime */
    if(activity.activityType === eventActivityType.PAIRING) {
      const releaseIndex = releaseActivities.findIndex(releaseActivity => {
        return convertToDateTime(releaseActivity.end_date, formatNumToTime(releaseActivity.checkOut.value)) === activityStartDateTime;
      });
      if(releaseIndex !== -1) {
        activity.release = [{...releaseActivities[releaseIndex], releaseType: 'start'}];
        activity.hasStartRelease = true;
      }
    } 
    else if(activity.activityType === eventActivityType.RAP) {

      /* there could be multiple releases attached to the same activity, therefore we must loop */
      releaseActivities.forEach(release => {
        const releaseStartDateTime = convertToDateTime(release.start_date, formatNumToTime(release.checkIn.value));
        const releaseEndDateTime = convertToDateTime(release.end_date, formatNumToTime(release.checkOut.value));
        
        /* if the release falls inbetween the RAP start and end time */
        if(releaseStartDateTime >= activityStartDateTime && releaseEndDateTime <= activityEndDateTime) {
          if(activity.release) activity.release.push({...release, releaseType: 'middle'});
          else activity.release = [{...release, releaseType: 'middle'}]; 
        }

        /* if the release is at the beginning of the RAP */
        if(releaseEndDateTime === activityStartDateTime) {

          /* for only RAP events, find an element where the activities end_date === releases's startDate */
          const releaseEndRapIndex = blockMonthActivities.filter(blockMonthActivity => blockMonthActivity.activityType === eventActivityType.RAP).findIndex(blockMonthActivity => {
            return convertToDateTime(blockMonthActivity.end_date, formatNumToTime(blockMonthActivity.checkOut.value)) === releaseStartDateTime;
          });
          if(releaseEndRapIndex === -1){
            if(activity.release) activity.release.push({...release, releaseType: 'start'});
            else activity.release = [{...release, releaseType: 'start'}]; 
            activity.hasStartRelease = true; 
          }
        }

        /* if the release is at the end of the RAP */
        if(releaseStartDateTime === activityEndDateTime) {
          const pairingIndex = blockMonthActivities.filter(blockMonthActivity => blockMonthActivity.activityType === eventActivityType.PAIRING).findIndex(blockMonthActivity => {
            return convertToDateTime(blockMonthActivity.start_date, formatNumToTime(blockMonthActivity.checkIn.value)) === releaseEndDateTime;
          });
          if(pairingIndex === -1) {
            if(activity.release) activity.release.push({...release, releaseType: 'end'});
            else activity.release = [{...release, releaseType: 'end'}]; 
            activity.hasEndRelease = true; 
          }
        }
      })
    } 
  })
}

/**
 * @param blockMonthActivities 
 * @param blockDate 
 * @returns events that have an end date greater than the blockDate start 
 */
export const removeEventsNotInBlockMonth = (blockMonthActivities, blockDate) => {
  return blockMonthActivities.filter(a => convertToDateTime(a.end_date, a.checkOut.value) > convertToDateTime(blockDate.start, '00:00'));
}

export const sortBlockMonthActivities = (blockMonthActivities) => {
  blockMonthActivities.forEach(activity => activity.startDateDaysTime = convertToDateTime(activity.start_date, formatNumToTime(activity.checkIn.value)));
  if(blockMonthActivities.length > 1) blockMonthActivities.sort((x, y) => x.startDateDaysTime - y.startDateDaysTime);
}